// Text template {{variables}}: spelling-tolerant fill for the texting windows.
// Mirrors fex-platform-service src/utils/sms-variables.util.ts (which also fills at send time); keep the two in step.

/** The " (2)" an import adds so two same-name leads can be told apart. Never goes to the lead. */
export const stripNameTag = (name?: string | null): string => (name ?? "").replace(/\s*\(\d+\)\s*$/, "").trim();

const ALIASES: Record<string, string> = {
  firstname: "first_name",
  lastname: "last_name",
  fullname: "name",
  full_name: "name",
  lead_name: "name",
  zipcode: "zip_code",
  zip: "zip_code",
};

/** {{First Name}}, {{firstName}}, {{first-name}}, {{FIRST_NAME}} and {{firstname}} all mean first_name. */
export const variableKey = (raw: string): string => {
  const key = raw.trim().replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase().replace(/[\s-]+/g, "_");
  return ALIASES[key] ?? key;
};

/** Fills each {{variable}} that has a value. Empty or unknown ones stay, for the server to fill or stop. */
export const fillVariables = (text: string, values: Record<string, string | null | undefined>): string =>
  text.replace(/\{\{([^{}]+)\}\}/g, (match, raw: string) => values[variableKey(raw)] || match);

/** Tag and stray trailing commas off a name part: 70k leads came in with first_name "Carlos,". */
const cleanPart = (s?: string | null): string => stripNameTag(s).replace(/[\s,]+$/, "");

/** "KATHERINE O'BRIEN-SMITH" → "Katherine O'Brien-Smith". Mixed case, 1–2 letter words (JD) and roman numerals stay. */
const unshout = (s: string): string =>
  s !== s.toUpperCase() || s === s.toLowerCase()
    ? s
    : s
        .split(" ")
        .map((w) => (w.replace(/[^A-Z]/g, "").length < 3 || /^[IVX]+\.?$/.test(w) ? w : w.toLowerCase().replace(/(^|['-])(\S)/g, (_, sep: string, ch: string) => sep + ch.toUpperCase())))
        .join(" ");

/** What imports and agents put where a name should be: "Lead 0412 (Row 88)", "NONE", "Mr". */
const NOT_A_NAME = new Set(["lead", "test", "none", "na", "n/a", "null", "unknown", "customer", "client", "mr", "mrs", "ms", "miss", "dr", "-"]);

/** A lead's name variables, clean. First name falls back to the name's first word; a placeholder means no name. */
export const leadNameVariables = (lead?: { name?: string | null; firstName?: string | null; lastName?: string | null } | null) => {
  const first = unshout(cleanPart(lead?.firstName));
  const last = unshout(cleanPart(lead?.lastName));
  // "Carlos, Martinez" → "Carlos Martinez".
  const name = unshout(cleanPart(lead?.name).replace(/^([^\s,]+),\s*/, "$1 ")) || [first, last].filter(Boolean).join(" ");
  const [firstWord = "", ...rest] = name.split(/\s+/);
  const firstName = first || firstWord;
  const key = firstName.toLowerCase().replace(/\.$/, "");
  if (!key || NOT_A_NAME.has(key)) return { name: "", first_name: "", last_name: "" };
  return { name, first_name: firstName, last_name: last || rest.join(" ") };
};
